/**
 * useDeepgramVoiceAgent Hook
 * WebSocket-based voice agent for homework and exercise tutoring
 * Replaces old REST API approach with real-time streaming
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { audioManager } from '../utils/audioManager.js';
import { voiceAssistantEventBus, VOICE_EVENTS } from '../utils/voiceAssistantEventBus.js';

export const useDeepgramVoiceAgent = ({
  sessionId, // homework sessionId, exercise attemptId, or userId for general help
  problemId = null, // For homework: problemId, for exercises: partId
  type = 'homework', // 'homework', 'exercise', or 'general'
  pageContext = 'unknown',
  pageContextData = null,
  currentWorkspace = '', // NEW: Current workspace content for auto-sync on utterance end
  onTranscript,
  onResponse,
  onError,
  shouldConnect = true // NEW: Control when to connect (default true for backward compatibility)
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [utteranceInProgress, setUtteranceInProgress] = useState(false);

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioElementRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingAudioRef = useRef(false);
  const shouldSendAudioFlagRef = useRef(null); // Controls whether to send mic audio to Deepgram
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0); // Track reconnection attempts for exponential backoff
  const shouldReconnectRef = useRef(true);
  const shouldConnectRef = useRef(shouldConnect);
  const disconnectRef = useRef(() => {});
  const userRequestedMicRef = useRef(false); // Tracks whether user expects mic to be active
  const lastSentContextRef = useRef(null);
  const pendingAutoListenRef = useRef(false);
  const hasAutoListenedRef = useRef(false);
  const silenceTimerRef = useRef(null); // Timer for client-side utterance closure
  const lastSpeechActivityRef = useRef(0); // Timestamp of last detected speech
  const utteranceStartTimeRef = useRef(0); // Timestamp when current utterance started
  const autoResumeMicRef = useRef(true);
  const pendingSpeechQueueRef = useRef([]);
  const ensureSocketOpenRef = useRef(null);
  const ensureSocketOpenInProgressRef = useRef(false);
  const currentWorkspaceRef = useRef(currentWorkspace); // Track current workspace for auto-sync
  const lastSentWorkspaceRef = useRef(''); // Track last sent workspace to prevent duplicates
  const workspaceSourceRef = useRef('student'); // Track whether workspace came from 'agent' or 'student'
  const lastTranscriptRef = useRef(''); // Track last transcript to prevent workspace send on background noise

  useEffect(() => {
    shouldConnectRef.current = shouldConnect;
  }, [shouldConnect]);

  // Sync workspace ref with prop changes
  useEffect(() => {
    // Only update if it's different and mark as student source
    // (agent-sourced updates happen directly in workspace_update handler)
    if (currentWorkspaceRef.current !== currentWorkspace && workspaceSourceRef.current !== 'agent') {
      currentWorkspaceRef.current = currentWorkspace;
      workspaceSourceRef.current = 'student'; // Prop changes are from student typing
    }
  }, [currentWorkspace]);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Reduced logging to prevent console spam
  // console.log('🎯 useDeepgramVoiceAgent initialized', { sessionId, problemId, type, pageContext });

  // Initialize Web Audio API for smooth playback
  const audioContextForPlaybackRef = useRef(null);
  const audioSourceNodeRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const flushPendingSpeechQueue = useCallback(() => {
    if (!pendingSpeechQueueRef.current.length) return;
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    if (!shouldSendAudioFlagRef.current?.current) return;

    try {
      pendingSpeechQueueRef.current.forEach((buffer) => {
        socket.send(buffer);
      });
      pendingSpeechQueueRef.current.length = 0;
    } catch (err) {
      console.error('❌ Failed to flush pending speech audio:', err);
    }
  }, []);

  /**
   * Play audio chunk using Web Audio API for smooth continuous playback
   * Optimized for minimal gaps without introducing artifacts
   */
  const playNextAudio = useCallback(async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingAudioRef.current = false;
      setIsAgentSpeaking(false);
      return;
    }

    if (!audioContextForPlaybackRef.current) {
      // Create AudioContext for playback (separate from mic capture)
      audioContextForPlaybackRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
      nextPlayTimeRef.current = audioContextForPlaybackRef.current.currentTime;
    }

    isPlayingAudioRef.current = true;
    setIsAgentSpeaking(true);

    const audioBase64 = audioQueueRef.current.shift();
    
    try {
      // Decode base64 to Int16 PCM data
      const binaryString = atob(audioBase64);
      if (binaryString.length < 2) {
        console.warn('⚠️ Audio chunk too small to decode');
        playNextAudio();
        return;
      }

      const evenLength = binaryString.length - (binaryString.length % 2);
      if (evenLength !== binaryString.length) {
        console.warn('⚠️ Audio chunk byte length not even, truncating last byte', {
          originalLength: binaryString.length,
          truncatedLength: evenLength
        });
      }

      const bytes = new Uint8Array(evenLength);
      for (let i = 0; i < evenLength; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert Int16 array to Float32 array for Web Audio API
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0; // Convert to -1.0 to 1.0
      }

      // Create audio buffer
      const audioContext = audioContextForPlaybackRef.current;
      const audioBuffer = audioContext.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      // Create source node and schedule playback
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      // Schedule this chunk to play immediately after the previous one
      const currentTime = audioContext.currentTime;
      const playTime = Math.max(nextPlayTimeRef.current, currentTime);
      source.start(playTime);
      
      // Update next play time (seamless continuation)
      nextPlayTimeRef.current = playTime + audioBuffer.duration;

      // When this chunk ends, play the next one
      source.onended = () => {
        playNextAudio();
      };

      // Eagerly start playing next chunk if queue is building up
      if (audioQueueRef.current.length > 3) {
        // Don't wait for onended, schedule next chunk immediately
        setTimeout(() => playNextAudio(), 0);
      }

    } catch (err) {
      console.error('Failed to process audio chunk:', err);
      playNextAudio();
    }
  }, []);

  /**
   * Queue audio chunk for playback
   */
  const queueAudio = useCallback((audioBase64) => {
    audioQueueRef.current.push(audioBase64);
    
    // Start playback if not already playing
    if (!isPlayingAudioRef.current) {
      playNextAudio();
    }
  }, [playNextAudio]);

  /**
   * Handle messages from server
   */
  const handleServerMessage = useCallback((message) => {
    console.log('📨 Server message:', message.type);

    switch (message.type) {
      case 'agent_session_replaced': {
        console.log('♻️ Agent session replaced', message);
        shouldReconnectRef.current = false;
        if (disconnectRef.current) {
          disconnectRef.current();
        }
        break;
      }

      case 'agent_connected':
        console.log('✅ Agent connected');
        break;

      case 'agent_thinking':
        setIsAgentThinking(true);
        break;

      case 'conversation_text': {
        const isUser = message.role === 'user';
        const displayContent = message.content || '';

        // Backend now handles all JSON parsing for tutor mode
        // Just display the content directly
        setConversationHistory(prev => [
          ...prev,
          {
            role: isUser ? 'student' : 'tutor',
            content: displayContent
          }
        ]);

        if (isUser) {
          // Track user transcript to prevent workspace send on background noise
          lastTranscriptRef.current = displayContent;
          setTranscription(displayContent);
          if (onTranscript) onTranscript(displayContent);
        } else if (onResponse) {
          onResponse(displayContent);
        }
        break;
      }

      case 'user_speaking':
        console.log('🎤 User speaking');
        break;

      case 'agent_speaking':
        // Agent started or stopped speaking
        setIsAgentSpeaking(message.speaking);
        if (!message.speaking) {
          setIsAgentThinking(false);
          if (!hasAutoListenedRef.current) {
            pendingAutoListenRef.current = true;
          }
          // Removed: audio context suspend/resume - keeping audio pipeline stable
          console.log('✅ Agent finished speaking - audio context remains running');
        }
        break;

      case 'audio':
      case 'audio_chunk':
        // Queue audio for playback
        if (message.audio) {
          queueAudio(message.audio);
        }
        break;

      case 'agent_audio_done':
        setIsAgentThinking(false);
        setIsAgentSpeaking(false);
        console.log('✅ Agent finished speaking - ready for next user input');
        // Removed: Flush message - letting Deepgram manage VAD state naturally
        break;

      case 'settings_applied':
        console.log('⚙️ Agent settings applied');
        break;

      case 'workspace_update': {
        // Backend sends workspace content separately for tutor mode
        console.log('📝 Workspace update from backend (agent):', message.workspaceContent);
        if (message.workspaceContent !== undefined) {
          // Mark this workspace as coming from the agent (don't echo it back)
          workspaceSourceRef.current = 'agent';
          currentWorkspaceRef.current = message.workspaceContent;
          
          voiceAssistantEventBus.emit(VOICE_EVENTS.WORKSPACE_UPDATE_FROM_AGENT, {
            sessionId,
            workspaceContent: message.workspaceContent
          });
        }
        break;
      }

      case 'problem_completed': {
        console.log('🎯 Problem completed signal:', message);
        voiceAssistantEventBus.emit(VOICE_EVENTS.PROBLEM_COMPLETED, {
          sessionId,
          status: message.status || 'complete',
          answer: message.answer || null,
          summary: message.summary || null
        });
        break;
      }

      case 'test':
        // Backend connectivity heartbeat - no action needed
        break;

      case 'error': {
        const errorMessage = message.message || message.error || 'Unknown voice agent error';
        const errorCode = message.code || message.error_code || null;

        // Deepgram emits CLIENT_MESSAGE_TIMEOUT when it hasn't received mic audio.
        // Treat this as a soft warning so the tutor UI can continue to function.
        if (errorCode === 'CLIENT_MESSAGE_TIMEOUT' || /waited too long for a websocket message/i.test(errorMessage)) {
          console.warn('⚠️ Voice agent idle timeout warning', { errorCode, errorMessage });
          setIsAgentThinking(false);
          setIsAgentSpeaking(false);
          return;
        }

        console.error('❌ Agent error:', { errorMessage, errorCode });
        setError(errorMessage);
        if (onError) onError(new Error(errorMessage));
        break;
      }

      case 'agent_action':
        if (message.action) {
          voiceAssistantEventBus.emit(VOICE_EVENTS.AGENT_ACTION, {
            action: message.action,
            metadata: message.metadata || null
          });
        }
        break;

      case 'navigate_to_problem': {
        console.log('🧭 Navigate to problem:', message.problemNumber, message.problemId);
        // Emit navigation event for the page component to handle
        voiceAssistantEventBus.emit(VOICE_EVENTS.NAVIGATE_TO_PROBLEM, {
          sessionId: message.sessionId,
          problemId: message.problemId,
          problemNumber: message.problemNumber,
          metadata: message.metadata || null
        });
        break;
      }
      case 'open_dino_help': {
        voiceAssistantEventBus.emit(VOICE_EVENTS.OPEN_DINO_HELP, {
          sessionId: message.sessionId,
          metadata: message.metadata || null
        });
        break;
      }
      case 'redirect_to_homework_upload': {
        voiceAssistantEventBus.emit(VOICE_EVENTS.REDIRECT_TO_HOMEWORK_UPLOAD, {
          sessionId: message.sessionId,
          metadata: message.metadata || null
        });
        break;
      }

      case 'agent_disconnected':
        setIsConnected(false);
        break;

      default:
        console.log('Unknown message type:', message.type);
    }
  }, [onTranscript, onResponse, onError, queueAudio, sessionId]);

  /**
   * Connect to backend WebSocket
   */
  const connect = useCallback(() => {
    clearReconnectTimeout();

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('⚠️ WebSocket already connected');
      return;
    }

    shouldReconnectRef.current = true;

    try {
      // Construct WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = import.meta.env.VITE_API_BASE_URL?.replace(/^https?:\/\//, '') || 'localhost:3001';
      
      // Phase 3: Support general help endpoint
      let endpoint;
      if (type === 'general') {
        endpoint = `/api/voice-agent/general/${sessionId}`; // sessionId is userId for general help
      } else if (type === 'homework') {
        endpoint = `/api/voice-agent/homework/${sessionId}${problemId ? `/${problemId}` : ''}`;
      } else {
        endpoint = `/api/voice-agent/exercise/${sessionId}/${problemId}`;
      }
      
      const wsUrl = `${wsProtocol}//${host}${endpoint}`;
      
      console.log('🔗 Connecting to voice agent:', wsUrl, { type, sessionId, problemId });
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      const scheduleReconnect = (reason = 'disconnect') => {
        if (!shouldReconnectRef.current || !shouldConnectRef.current) {
          console.log('⏭️ Skipping auto-reconnect', {
            reason,
            shouldReconnect: shouldReconnectRef.current,
            shouldConnect: shouldConnectRef.current
          });
          return;
        }

        clearReconnectTimeout();
        
        reconnectAttemptsRef.current += 1;
        
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped at 30s)
        const maxDelay = 30000;
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttemptsRef.current - 1),
          maxDelay
        );
        
        console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}, reason: ${reason})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (shouldReconnectRef.current && shouldConnectRef.current) {
            console.log('🔄 Attempting reconnect...');
            connect();
          }
        }, delay);
      };

      ws.onopen = () => {
        console.log('✅ Voice agent connected');
        reconnectAttemptsRef.current = 0; // Reset backoff on successful connection
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleServerMessage(message);
        } catch (err) {
          console.error('Failed to parse server message:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('❌ WebSocket error:', err);
        setError('Connection error. Will retry...');
        setIsConnected(false);
        setIsListening(false);
        if (onError) onError(err);

        scheduleReconnect('error');
      };

      ws.onclose = () => {
        console.log('🔌 Voice agent disconnected');
        setIsConnected(false);
        setIsListening(false);

        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        scheduleReconnect('disconnect');
      };

    } catch (err) {
      console.error('❌ Failed to connect:', err);
      setError('Failed to connect to voice agent');
      if (onError) onError(err);
    }
  }, [sessionId, problemId, type, onError, handleServerMessage, clearReconnectTimeout]);

  /**
   * Start capturing microphone and sending to backend
   */
  const startListening = useCallback(async () => {
    autoResumeMicRef.current = true;
    userRequestedMicRef.current = true;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ Cannot start microphone yet - WebSocket not connected');
      return;
    }

    try {
      console.log('🎤 Starting microphone capture');

      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        }
      });

      audioStreamRef.current = stream;

      // Create audio context
      const audioContext = new AudioContext({ sampleRate: 48000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      let audioChunkCount = 0;
      let lastAudioSentTime = Date.now();
      let silentChunks = 0;
      const AUDIO_HEALTH_CHECK_INTERVAL = 100;
      const SILENCE_THRESHOLD = 0.012; // Slightly higher to filter background hum
      const SPEECH_THRESHOLD = 0.025;  // Require louder RMS to mark speech
      const MAX_SILENT_CHUNKS = 200;
      const PREBUFFER_CHUNKS = 10; // ~200ms context
      const SILENCE_CHUNKS_TO_CLOSE = 8; // ~160ms
      const MAX_UTTERANCE_DURATION_MS = 30000;
      const MAX_PENDING_SPEECH_BUFFERS = 100; // Max buffers to queue when WebSocket is reconnecting
      
      const shouldSendAudioRef = { current: true };
      shouldSendAudioFlagRef.current = shouldSendAudioRef;
      const audioPreBuffer = [];
      let speechActive = false;
      let silenceChunkCounter = 0;

      processor.onaudioprocess = (e) => {
        // ✅ FIX: Early exit if WebSocket is closed/null
        if (!wsRef.current) {
          return;
        }
        
        const inputData = e.inputBuffer.getChannelData(0);
        
        // Calculate audio energy (RMS) to detect speech vs silence
        let sumSquares = 0;
        for (let i = 0; i < inputData.length; i++) {
          sumSquares += inputData[i] * inputData[i];
        }
        const rms = Math.sqrt(sumSquares / inputData.length);
        const hasSpeech = rms > SPEECH_THRESHOLD;
        const isSilent = rms < SILENCE_THRESHOLD;
        
        if (isSilent) {
          silentChunks++;
        } else {
          silentChunks = 0;
        }
        
        // Maintain RMS-based speech gating
        if (hasSpeech) {
          lastSpeechActivityRef.current = Date.now();
          silenceChunkCounter = 0;
          
          if (!speechActive) {
            speechActive = true;
            setUtteranceInProgress(true);
            utteranceStartTimeRef.current = Date.now();
            console.log('🎤 Speech detected - streaming audio', { rms: rms.toFixed(4) });
            
            const socketReady = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
            if (socketReady && shouldSendAudioRef.current) {
              audioPreBuffer.forEach(buffer => wsRef.current.send(buffer));
              pendingSpeechQueueRef.current.length = 0;
            } else {
              pendingSpeechQueueRef.current.push(...audioPreBuffer);
              if (pendingSpeechQueueRef.current.length > MAX_PENDING_SPEECH_BUFFERS) {
                pendingSpeechQueueRef.current.splice(
                  0,
                  pendingSpeechQueueRef.current.length - MAX_PENDING_SPEECH_BUFFERS
                );
              }
              const ensureSocket = ensureSocketOpenRef.current;
              if (ensureSocket) ensureSocket();
            }
            audioPreBuffer.length = 0;
          }
        } else if (speechActive) {
          silenceChunkCounter++;
          const utteranceDuration = Date.now() - utteranceStartTimeRef.current;
          const shouldCloseForSilence = silenceChunkCounter >= SILENCE_CHUNKS_TO_CLOSE;
          const hitMaxDuration = utteranceDuration >= MAX_UTTERANCE_DURATION_MS;
          
          if (shouldCloseForSilence || hitMaxDuration) {
            speechActive = false;
            setUtteranceInProgress(false);
            silenceChunkCounter = 0;
            console.log('🤫 Speech ended', {
              reason: hitMaxDuration ? 'max_duration' : 'silence',
              utteranceDuration
            });
            
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && shouldSendAudioRef.current) {
              const silenceBuffer = new Int16Array(960).fill(0);
              wsRef.current.send(silenceBuffer.buffer);
              console.log('📤 Sent silence buffer to finalize utterance');
              
              // ✅ NEW: Send workspace content after utterance ends (for tutor mode)
              // GUARD 1: Only send if we received a real transcript (prevents background noise echo)
              if (!lastTranscriptRef.current || !lastTranscriptRef.current.trim()) {
                console.log('⏭️ Skipping workspace send - no transcript from this utterance (background noise)', {
                  hasTranscript: !!lastTranscriptRef.current,
                  transcriptLength: lastTranscriptRef.current?.length || 0
                });
                // Clear transcript for next cycle
                lastTranscriptRef.current = '';
                return;
              }
              
              // GUARD 2: Only send if workspace has content
              if (currentWorkspaceRef.current && currentWorkspaceRef.current.trim()) {
                const trimmedWorkspace = currentWorkspaceRef.current.trim();
                const workspaceSource = workspaceSourceRef.current;
                
                // GUARD 3: Don't send workspace if it came from the agent (prevents echo loop)
                if (workspaceSource === 'agent') {
                  console.log('⏭️ Skipping workspace send - came from agent (not student edit)', {
                    workspaceLength: trimmedWorkspace.length,
                    transcript: lastTranscriptRef.current
                  });
                  // DON'T reset to 'student' here - keep it 'agent' until student actually edits
                  // Reset transcript for next cycle
                  lastTranscriptRef.current = '';
                  return;
                }
                
                // GUARD 4: Only send if workspace actually changed
                if (trimmedWorkspace !== lastSentWorkspaceRef.current) {
                  // ✅ Update tracking IMMEDIATELY to prevent race condition
                  lastSentWorkspaceRef.current = trimmedWorkspace;
                  
                  // Small delay to ensure silence buffer is processed first
                  setTimeout(() => {
                    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                      console.log('📝 Sending workspace after utterance (student edit)', {
                        length: currentWorkspaceRef.current.length,
                        preview: currentWorkspaceRef.current.substring(0, 100),
                        transcript: lastTranscriptRef.current
                      });
                      wsRef.current.send(JSON.stringify({
                        type: 'workspace_update',
                        workspaceContent: currentWorkspaceRef.current
                      }));
                    }
                  }, 100);
                } else {
                  console.log('⏭️ Skipping workspace send - no changes since last send', {
                    transcript: lastTranscriptRef.current
                  });
                }
              }
              
              // Clear transcript for next utterance cycle
              lastTranscriptRef.current = '';
            }
            pendingSpeechQueueRef.current.length = 0;
          }
        }
        
        // Convert Float32Array to Int16Array (16-bit PCM)
        const int16Data = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        audioPreBuffer.push(int16Data.buffer);
        if (audioPreBuffer.length > PREBUFFER_CHUNKS) {
          audioPreBuffer.shift();
        }
        
        const shouldStreamChunk = speechActive && shouldSendAudioRef.current;
        const socketReady = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
        
        if (socketReady && shouldStreamChunk) {
          if (pendingSpeechQueueRef.current.length) {
            flushPendingSpeechQueue();
          }
          wsRef.current.send(int16Data.buffer);
          lastAudioSentTime = Date.now();
          
          // Health monitoring logs
          audioChunkCount++;
          if (audioChunkCount % AUDIO_HEALTH_CHECK_INTERVAL === 0) {
            const timeSinceLastSend = Date.now() - lastAudioSentTime;
            console.log(`🔊 Audio health check:`, {
              totalChunks: audioChunkCount,
              currentRMS: rms.toFixed(4),
              isSilent,
              hasSpeech,
              consecutiveSilentChunks: silentChunks,
              utteranceActive: utteranceInProgress,
              timeSinceLastSend: `${timeSinceLastSend}ms`,
              wsState: wsRef.current.readyState,
              shouldSend: shouldSendAudioRef.current
            });
            
            // Warn if extended silence detected
            if (silentChunks > MAX_SILENT_CHUNKS) {
              console.warn('⚠️ Extended silence detected - possible mic issue', {
                silentDuration: `~${Math.round(silentChunks * 4096 / 48000)}s`,
                silentChunks
              });
            }
          }
        } else if (!shouldStreamChunk) {
          // Audio NOT being sent - log why
          audioChunkCount++;
          if (audioChunkCount % AUDIO_HEALTH_CHECK_INTERVAL === 0) {
            console.warn('⚠️ Audio NOT being sent:', {
              wsOpen: wsRef.current && wsRef.current.readyState === WebSocket.OPEN,
              shouldSend: shouldSendAudioRef.current,
              audioChunkCount,
              currentRMS: rms.toFixed(4)
            });
          }
        } else if (speechActive && shouldSendAudioRef.current) {
          pendingSpeechQueueRef.current.push(int16Data.buffer);
          if (pendingSpeechQueueRef.current.length > MAX_PENDING_SPEECH_BUFFERS) {
            pendingSpeechQueueRef.current.shift();
          }
          const ensureSocket = ensureSocketOpenRef.current;
          if (ensureSocket) ensureSocket();
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      console.log('✅ Microphone capture started');
      setIsListening(true);

    } catch (err) {
      console.error('❌ Failed to start microphone:', err);
      setError('Failed to access microphone');
      if (onError) onError(err);
    }
  }, [onError]);

  /**
   * Stop capturing microphone
   */
  const stopListening = useCallback((options = {}) => {
    const { preservePreference = false } = options;
    if (!preservePreference) {
      userRequestedMicRef.current = false;
      autoResumeMicRef.current = false;
    }
    console.log('🛑 Stopping microphone capture');

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    pendingSpeechQueueRef.current.length = 0;
    setIsListening(false);
  }, []);

  /**
   * Update workspace content (sends to backend to update prompt)
   */
  const updateWorkspace = useCallback((workspaceContent) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const trimmedWorkspace = (workspaceContent || '').trim();
      
      // Check if workspace has actually changed
      if (trimmedWorkspace === lastSentWorkspaceRef.current) {
        console.log('⏭️ Skipping workspace send - no changes since last send');
        return;
      }

      // Mark as student update (manual call to updateWorkspace)
      workspaceSourceRef.current = 'student';
      
      console.log('📝 Sending workspace update (manual/student)', {
        contentLength: workspaceContent?.length || 0
      });

      wsRef.current.send(JSON.stringify({
        type: 'workspace_update',
        workspaceContent
      }));
      
      lastSentWorkspaceRef.current = trimmedWorkspace;
    }
  }, []);

  const sendCustomMessage = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') {
      console.warn('⚠️ sendCustomMessage: Invalid payload', payload);
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('📤 Sending custom message via WebSocket', payload);
      wsRef.current.send(JSON.stringify(payload));
    } else {
      console.warn('⚠️ sendCustomMessage: WebSocket not open', {
        hasWs: !!wsRef.current,
        readyState: wsRef.current?.readyState
      });
    }
  }, []);

  const sendOcrSummary = useCallback((summary) => {
    console.log('📤 Sending OCR summary to backend via WebSocket', summary);
    sendCustomMessage({
      type: 'ocr_summary',
      summary
    });
  }, [sendCustomMessage]);

  const sendValidationStatus = useCallback((status) => {
    sendCustomMessage({
      type: 'validation_status',
      status
    });
  }, [sendCustomMessage]);

  /**
   * Disconnect from voice agent
   */
  const disconnect = useCallback((options = {}) => {
    const { disableRetry = true } = options;
    console.log('🔌 Disconnecting voice agent');

    if (disableRetry) {
      shouldReconnectRef.current = false;
    }
    
    clearReconnectTimeout();
    
    // Clean up silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    
    // ✅ FIX: Close WebSocket FIRST to stop any in-flight audio immediately
    const ws = wsRef.current;
    wsRef.current = null; // Null immediately so onaudioprocess stops sending
    setIsConnected(false);
    
    if (ws) {
      try {
        ws.close();
      } catch (error) {
        console.warn('⚠️ Error closing WebSocket:', error);
      }
    }
    
    // Then stop audio capture (this will clean up any pending audio events)
    stopListening({ preservePreference: true });

    // Stop any playing audio
    audioManager.stopAll();
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;
    
    // Close playback AudioContext
    if (audioContextForPlaybackRef.current) {
      audioContextForPlaybackRef.current.close();
      audioContextForPlaybackRef.current = null;
    }
  }, [stopListening, clearReconnectTimeout]);

  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);

  useEffect(() => {
    const unsubscribe = voiceAssistantEventBus.on(
      VOICE_EVENTS.OCR_PROCESSING_STARTED,
      (data = {}) => {
        if (!sessionId || data.sessionId !== sessionId) {
          return;
        }
        sendCustomMessage({
          type: 'upload_status',
          status: 'ocr_processing_started',
          estimatedDuration: data.estimatedDuration ?? null
        });
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [sessionId, sendCustomMessage]);

  // Auto-connect on mount
  useEffect(() => {
    if (shouldConnect) {
      shouldReconnectRef.current = true;
      connect();
    }

    return () => {
      disconnect();
    };
  }, [shouldConnect, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 useDeepgramVoiceAgent cleanup');
      disconnect();
    };
  }, [disconnect]);

  // Control microphone audio sending based on agent speaking state
  // Pause mic audio while agent is speaking to avoid echo/feedback
  useEffect(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!shouldSendAudioFlagRef.current) {
      return;
    }

    // Always pause immediately when the agent starts speaking
    if (isAgentSpeaking) {
      console.log('🔇 Agent speaking - pausing microphone audio');
      shouldSendAudioFlagRef.current.current = false;
      return;
    }

    // When the agent stops, wait briefly before resuming to avoid capturing trailing audio
    let resumeTimer = setTimeout(() => {
      console.log('🎤 Agent stopped speaking - resuming microphone audio (after 1s)');
      shouldSendAudioFlagRef.current.current = true;
      
      // 🐛 FIX: Verify audio context is still running
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log('⚠️ Audio context suspended - resuming...');
        audioContextRef.current.resume().then(() => {
          console.log('✅ Audio context resumed');
        }).catch(err => {
          console.error('❌ Failed to resume audio context:', err);
        });
      }
      
      // 🐛 FIX: Log current audio stream state
      if (audioStreamRef.current) {
        const tracks = audioStreamRef.current.getAudioTracks();
        console.log('🔍 Audio stream tracks:', tracks.map(t => ({
          id: t.id,
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState
        })));
      } else {
        console.warn('⚠️ Audio stream is null - microphone may need restart');
      }
    }, 1000);

    return () => {
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    };
  }, [isAgentSpeaking]);

  // Note: KeepAlive is now handled entirely by the backend (every 4.5 seconds)
  // Frontend no longer needs to send KeepAlive messages

  // Manual reconnect function
  const reconnect = useCallback(() => {
    console.log('🔄 Manual reconnect requested');
    disconnect({ disableRetry: false });
    setTimeout(() => {
      if (shouldConnectRef.current) {
        shouldReconnectRef.current = true;
        connect();
      }
    }, 500);
  }, [disconnect, connect]);

  const ensureSocketOpen = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      flushPendingSpeechQueue();
      return true;
    }

    if (ensureSocketOpenInProgressRef.current) {
      return false;
    }

    ensureSocketOpenInProgressRef.current = true;
    console.warn('⚠️ Voice agent WebSocket not open - attempting automatic reconnect before streaming audio');
    reconnect();

    return await new Promise((resolve) => {
      const start = Date.now();
      const poll = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          ensureSocketOpenInProgressRef.current = false;
          flushPendingSpeechQueue();
          resolve(true);
          return;
        }

        if (Date.now() - start > 5000) {
          ensureSocketOpenInProgressRef.current = false;
          console.warn('⚠️ Voice WebSocket did not reopen within timeout');
          resolve(false);
          return;
        }

        setTimeout(poll, 100);
      };

      poll();
    });
  }, [reconnect, flushPendingSpeechQueue]);

  useEffect(() => {
    ensureSocketOpenRef.current = ensureSocketOpen;
  }, [ensureSocketOpen]);

  useEffect(() => {
    if (
      autoResumeMicRef.current &&
      userRequestedMicRef.current &&
      !isAgentSpeaking &&
      isConnected &&
      !isListening &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      !audioStreamRef.current
    ) {
      startListening();
    }
  }, [isAgentSpeaking, isConnected, isListening, startListening]);

  useEffect(() => {
    if (
      autoResumeMicRef.current &&
      pendingAutoListenRef.current &&
      !isAgentSpeaking &&
      isConnected &&
      !isListening &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      !audioStreamRef.current
    ) {
      pendingAutoListenRef.current = false;
      hasAutoListenedRef.current = true;
      userRequestedMicRef.current = true;
      startListening();
    } else if (!isAgentSpeaking && pendingAutoListenRef.current) {
      pendingAutoListenRef.current = false;
    }
  }, [isAgentSpeaking, isConnected, isListening, startListening]);

  useEffect(() => {
    if (!pageContextData) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const payload = {
      type: 'page_context',
      ...pageContextData
    };
    const serialized = JSON.stringify(payload);
    if (serialized === lastSentContextRef.current) {
      return;
    }
    lastSentContextRef.current = serialized;
    sendCustomMessage(payload);
  }, [pageContextData, sendCustomMessage, isConnected]);

  return {
    // State
    isConnected,
    isAgentThinking,
    isAgentSpeaking,
    isListening,
    transcription,
    conversationHistory,
    error,
    
    // Actions
    startListening,
    stopListening,
    updateWorkspace,
    connect,
    disconnect,
    reconnect, // NEW: Manual reconnect
    sendOcrSummary,
    sendValidationStatus,
    sendCustomMessage
  };
};

export default useDeepgramVoiceAgent;

